import React, { ComponentType } from 'react';
import {
  command,
  CommandFunction,
  findNodeAtSelection,
  isNodeOfType,
  keyBinding,
  KeyBindingProps,
  NodeSpecOverride,
  ProsemirrorAttributes,
  removeNodeAtPosition,
} from 'remirror';
import {
  ApplySchemaAttributes,
  extension,
  ExtensionTag,
  NodeExtension,
  NodeExtensionSpec,
} from '@remirror/core';
import { Selection, TextSelection } from '@remirror/pm/state';
import { NodeViewComponentProps } from '@remirror/extension-react-component';
import { EditorTodo } from './EditorTodo';

interface TodoOptions {}

@extension<TodoOptions>({
  defaultOptions: {},
  staticKeys: [],
})
export class TodoExtension extends NodeExtension<TodoOptions> {
  get name() {
    return 'todo' as const;
  }

  createTags() {
    return [ExtensionTag.Block];
  }

  createNodeSpec(
    extra: ApplySchemaAttributes,
    override: NodeSpecOverride,
  ): NodeExtensionSpec {
    return {
      attrs: {
        ...extra.defaults(),
        todoNodeId: { default: '' },
        pageId: { default: null },
      },
      selectable: true,
      draggable: false,
      // atom: true,
      content: 'text*',
      isolating: true,
      ...override,
      toDOM: (node) => {
        const attributes = {
          ...extra.dom(node),
          todo: 'one',
        };
        return ['div', 0];
      },
    };
  }

  @keyBinding({ shortcut: 'Enter' })
  handleEnterKey({ dispatch, tr, state }: KeyBindingProps): boolean {
    try {
      const { node } = findNodeAtSelection(state.selection);

      if (!isNodeOfType({ node, types: this.type })) {
        return false;
      }

      if (tr.selection.$from.node().textContent) {
        const end = tr.selection.$from.end();
        tr.insert(end, this.type.create({ pageId: node.attrs.pageId }));
        tr.setSelection(TextSelection.near(tr.doc.resolve(end + 2)));
        tr.scrollIntoView();
      } else {
        const range = tr.selection.$from.blockRange();
        if (range) {
          tr.setBlockType(range.start, range.end, state.schema.nodes.paragraph);
        }
      }
      dispatch?.(tr);
      return true;
    } catch (e) {
      // This happens when selection is bigger than node
      return false;
    }
  }

  @keyBinding({ shortcut: ['Backspace', 'Delete'] })
  handleBackspace({ dispatch, tr, state }: KeyBindingProps): boolean {
    try {
      const { node } = findNodeAtSelection(state.selection);

      if (!isNodeOfType({ node, types: this.type })) {
        return false;
      }

      const range = tr.selection.$from.blockRange();
      if (!tr.selection.$from.node().textContent.trim() && range) {
        tr.setBlockType(range.start, range.end, state.schema.nodes.paragraph);
        dispatch?.(tr);
        return true;
      }

      return false;
    } catch (e) {
      // This happens when selection is bigger than node
      return false;
    }
  }

  @keyBinding({ shortcut: 'ArrowUp' })
  handleUp({ dispatch, tr, state }: KeyBindingProps): boolean {
    try {
      const { node } = findNodeAtSelection(state.selection);

      if (!isNodeOfType({ node, types: this.type })) {
        return false;
      }

      const side = -1;
      const $head = tr.selection.$head;
      const nextPos = Selection.near(
        tr.doc.resolve(side > 0 ? $head.after() : $head.before()),
        side,
      );
      dispatch?.(tr.setSelection(nextPos));
      return true;
    } catch (e) {
      // This happens when selection is bigger than node
      return false;
    }
  }

  @keyBinding({ shortcut: 'ArrowDown' })
  handleDown({ dispatch, tr, state }: KeyBindingProps): boolean {
    try {
      const { node } = findNodeAtSelection(state.selection);

      if (!isNodeOfType({ node, types: this.type })) {
        return false;
      }

      const side = 1;
      const $head = tr.selection.$head;
      const nextPos = Selection.near(
        tr.doc.resolve(side > 0 ? $head.after() : $head.before()),
        side,
      );
      dispatch?.(tr.setSelection(nextPos));
      return true;
    } catch (e) {
      // This happens when selection is bigger than node
      return false;
    }
  }

  @command()
  insertTodo(attributes: TodoAttributes): CommandFunction {
    return ({ tr, dispatch }) => {
      dispatch?.(
        tr
          .insert(
            attributes.selection || tr.selection.from,
            this.type.create(attributes),
          )
          .scrollIntoView(),
      );
      return true;
    };
  }

  @command()
  toggleTodo(attributes: TodoAttributes): CommandFunction {
    return ({ tr, dispatch }) => {
      dispatch?.(
        tr
          .replaceWith(
            attributes.selection || tr.selection.from,
            (attributes.selection || tr.selection.from) + 1,
            this.type.create(attributes),
          )
          .scrollIntoView(),
      );
      return true;
    };
  }

  @command()
  setTodoNodeId(pos: number, id: string): CommandFunction {
    return ({ tr, state, dispatch }) => {
      const node = state.doc.nodeAt(pos);

      if (node && node.type === this.type) {
        dispatch?.(
          tr.setNodeMarkup(pos, undefined, {
            ...node.attrs,
            todoNodeId: id,
          }),
        );
        return true;
      }

      return false;
    };
  }

  @command()
  deleteTodo(pos: number): CommandFunction {
    return ({ tr, state, dispatch }) => {
      const node = state.doc.nodeAt(pos);

      if (node && node.type === this.type) {
        dispatch?.(removeNodeAtPosition({ pos, tr }));
        return true;
      }

      return false;
    };
  }

  ReactComponent: ComponentType<NodeViewComponentProps> = (props) => {
    return <EditorTodo {...props} />;
  };
}

export type TodoAttributes = ProsemirrorAttributes<TodoExtensionAttributes>;

export interface TodoExtensionAttributes {
  selection?: number;
  pageId: string;
}

declare global {
  namespace Remirror {
    interface AllExtensions {
      todo: TodoExtension;
    }
  }
}
